import { Box, Checkbox, FormControlLabel, Radio, Stack, Typography, alpha, useTheme } from '@mui/material';
import { ContactIdsChipsInput } from './ContactIdsChipsInput';
import { useAppSelector } from 'hooks/redux.hooks';
import { selectTranslations } from 'state/reducers/translation.reducers';
import {
	SYSTEM_MESSAGE_BROADCAST_AUDIENCE_OPTIONS,
	SYSTEM_MESSAGE_COMPANY_AUDIENCE_OPTIONS,
	SystemMessageAudienceType,
	type SystemMessageAudienceType as SystemMessageAudienceTypeValue,
} from 'constants/system-messages.constants';
import type { ISystemMessageCompanyContact, SystemMessageFormMode } from 'types/system-messages.types';
import { getContactDisplayName } from '../createForm.helpers';
import { getAudienceDescSx, getAudienceTitleSx } from '../createForm.styles';
import { CreateFormFieldLabel } from './CreateFormFieldLabel';

type CreateFormAudienceSectionProps = {
	mode: SystemMessageFormMode;
	audienceType: SystemMessageAudienceTypeValue;
	onAudienceTypeChange: (value: SystemMessageAudienceTypeValue) => void;
	contactIdsRaw: string;
	onContactIdsRawChange: (value: string) => void;
	companyContacts: ISystemMessageCompanyContact[];
	checkedContactIds: number[];
	onCompanyContactToggle: (contactId: number, checked: boolean) => void;
};

export const CreateFormAudienceSection = ({
	mode,
	audienceType,
	onAudienceTypeChange,
	contactIdsRaw,
	onContactIdsRawChange,
	companyContacts,
	checkedContactIds,
	onCompanyContactToggle,
}: CreateFormAudienceSectionProps) => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const { mediumGreen, forestGreen, inputFocusGreen, basicWhite } = theme.palette.root;
	const audienceTitleSx = getAudienceTitleSx(theme);
	const audienceDescSx = getAudienceDescSx(theme);
	const audienceOptions =
		mode === 'broadcast' ? SYSTEM_MESSAGE_BROADCAST_AUDIENCE_OPTIONS : SYSTEM_MESSAGE_COMPANY_AUDIENCE_OPTIONS;

	return (
		<Stack spacing={1}>
			<CreateFormFieldLabel label={t('page.systemMessages.field.audience')} />
			<Stack spacing={1}>
				{audienceOptions.map((option) => {
					const selected = audienceType === option.value;
					return (
						<Box
							key={option.value}
							onClick={() => onAudienceTypeChange(option.value)}
							sx={{
								display: 'flex',
								alignItems: 'flex-start',
								gap: '12px',
								padding: '14px 16px',
								borderRadius: '8px',
								background: selected ? alpha(forestGreen, 0.04) : basicWhite,
								border: `1.5px solid ${selected ? forestGreen : mediumGreen}`,
								cursor: 'pointer',
								transition: 'all 0.15s',
								'&:hover': { borderColor: inputFocusGreen },
							}}
						>
							<Radio
								checked={selected}
								value={option.value}
								sx={{
									padding: 0,
									mt: '2px',
									color: mediumGreen,
									'&.Mui-checked': { color: forestGreen },
								}}
							/>
							<Stack spacing={0.5} flex={1} sx={{ minWidth: 0, width: '100%' }}>
								<Typography sx={audienceTitleSx}>{t(option.titleKey)}</Typography>
								<Typography sx={audienceDescSx}>{t(option.descKey)}</Typography>
								{option.value === SystemMessageAudienceType.SPECIFIC_CONTACTS &&
									selected &&
									mode === 'broadcast' && (
										<Box
											sx={{
												mt: 1.5,
												width: '100%',
												minWidth: 0,
												maxWidth: '100%',
												overflow: 'hidden',
												border: `1px solid ${mediumGreen}`,
												borderRadius: '8px',
												padding: 2,
												background: basicWhite,
											}}
											onClick={(event) => event.stopPropagation()}
										>
											<ContactIdsChipsInput
												rawValue={contactIdsRaw}
												onRawChange={onContactIdsRawChange}
											/>
										</Box>
									)}
								{option.value === SystemMessageAudienceType.SPECIFIC_COMPANY_CONTACTS &&
									selected &&
									mode === 'company' && (
										<Box
											sx={{
												mt: 1.5,
												border: `1px solid ${mediumGreen}`,
												borderRadius: '8px',
												padding: 2,
												background: alpha(mediumGreen, 0.08),
											}}
											onClick={(event) => event.stopPropagation()}
										>
											<Stack spacing={0.5}>
												{companyContacts.map((contact) => (
													<FormControlLabel
														key={contact.id}
														control={
															<Checkbox
																checked={checkedContactIds.includes(contact.id)}
																onChange={(event) =>
																	onCompanyContactToggle(contact.id, event.target.checked)
																}
															/>
														}
														label={`${getContactDisplayName(contact)} (${contact.id})`}
													/>
												))}
											</Stack>
										</Box>
									)}
							</Stack>
						</Box>
					);
				})}
			</Stack>
		</Stack>
	);
};
